import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminCompanies from "./pages/admin/Companies";
import AdminMachines from "./pages/admin/Machines";
import AdminWorks from "./pages/admin/Works";
import AdminIncidents from "./pages/admin/Incidents";
import AdminTechnicians from "./pages/admin/Technicians";
import AdminCalendar from "./pages/admin/Calendar";
import AdminReports from "./pages/admin/Reports";
import AdminInvoices from "./pages/admin/Invoices";
import AdminTimeLogs from "./pages/admin/TimeLogs";
import TechDashboard from "./pages/technician/Dashboard";
import TechWorks from "./pages/technician/Works";
import TechIncidents from "./pages/technician/Incidents";
import TechTimeClock from "./pages/technician/TimeClock";
import ClientDashboard from "./pages/client/Dashboard";
import ClientMachines from "./pages/client/Machines";
import ClientWorks from "./pages/client/Works";
import ClientIncidents from "./pages/client/Incidents";
import ClientReports from "./pages/client/Reports";
import ClientInvoices from "./pages/client/Invoices";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-sky-500/20" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'technician' ? '/tech' : '/client';
    return <Navigate to={redirectPath} replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-sky-500/20" />
      </div>
    );
  }
  
  const getRedirectPath = (userObj) => {
    if (!userObj) return '/login';
    if (userObj.role === 'admin') return '/admin';
    if (userObj.role === 'technician') return '/tech';
    return '/client';
  };
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getRedirectPath(user)} replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to={getRedirectPath(user)} replace /> : <Register />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute allowedRoles={['admin']}><AdminCompanies /></ProtectedRoute>} />
      <Route path="/admin/machines" element={<ProtectedRoute allowedRoles={['admin']}><AdminMachines /></ProtectedRoute>} />
      <Route path="/admin/works" element={<ProtectedRoute allowedRoles={['admin']}><AdminWorks /></ProtectedRoute>} />
      <Route path="/admin/incidents" element={<ProtectedRoute allowedRoles={['admin']}><AdminIncidents /></ProtectedRoute>} />
      <Route path="/admin/technicians" element={<ProtectedRoute allowedRoles={['admin']}><AdminTechnicians /></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute allowedRoles={['admin']}><AdminCalendar /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin']}><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute allowedRoles={['admin']}><AdminInvoices /></ProtectedRoute>} />
      <Route path="/admin/timelogs" element={<ProtectedRoute allowedRoles={['admin']}><AdminTimeLogs /></ProtectedRoute>} />
      
      {/* Technician Routes */}
      <Route path="/tech" element={<ProtectedRoute allowedRoles={['technician']}><TechDashboard /></ProtectedRoute>} />
      <Route path="/tech/works" element={<ProtectedRoute allowedRoles={['technician']}><TechWorks /></ProtectedRoute>} />
      <Route path="/tech/incidents" element={<ProtectedRoute allowedRoles={['technician']}><TechIncidents /></ProtectedRoute>} />
      <Route path="/tech/timeclock" element={<ProtectedRoute allowedRoles={['technician']}><TechTimeClock /></ProtectedRoute>} />
      
      {/* Client Routes */}
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />
      <Route path="/client/machines" element={<ProtectedRoute allowedRoles={['client']}><ClientMachines /></ProtectedRoute>} />
      <Route path="/client/works" element={<ProtectedRoute allowedRoles={['client']}><ClientWorks /></ProtectedRoute>} />
      <Route path="/client/incidents" element={<ProtectedRoute allowedRoles={['client']}><ClientIncidents /></ProtectedRoute>} />
      <Route path="/client/reports" element={<ProtectedRoute allowedRoles={['client']}><ClientReports /></ProtectedRoute>} />
      <Route path="/client/invoices" element={<ProtectedRoute allowedRoles={['client']}><ClientInvoices /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
